import type { Channel, Lap, RaceChronoMeta, SessionData } from '@tla/types';

/**
 * Compact binary (de)serialisation for {@link SessionData}.
 *
 * Telemetry sessions hold several large `Float64Array`s (time, distance and one
 * per channel). JSON would bloat these ~2–3× and lose precision, so the wire/
 * disk format is:
 *
 *   [4 bytes: header length N (uint32 LE)]
 *   [N bytes: UTF-8 JSON header]
 *   [payload: concatenated Float64 buffers in the order named by the header]
 *
 * The JSON header carries all metadata (meta, laps, channel descriptors,
 * canonical speed id) plus each array's sample count, so the reader can slice
 * the payload back into typed arrays without copying semantics changing.
 */

const MAGIC = 'TLAB1';

interface ChannelHeader {
  id: string;
  name: string;
  unit: string;
  group: string;
  source: Channel['source'];
  derived?: boolean;
  length: number;
}

interface SessionHeader {
  magic: string;
  meta: RaceChronoMeta;
  laps: Lap[];
  canonicalSpeedId: string;
  timeLength: number;
  distanceLength: number;
  channels: ChannelHeader[];
}

/** Serialise a {@link SessionData} into a single `Uint8Array`. */
export function serializeSessionData(session: SessionData): Uint8Array {
  const header: SessionHeader = {
    magic: MAGIC,
    meta: session.meta,
    laps: session.laps,
    canonicalSpeedId: session.canonicalSpeedId,
    timeLength: session.time.length,
    distanceLength: session.distance.length,
    channels: session.channels.map((c) => ({
      id: c.id,
      name: c.name,
      unit: c.unit,
      group: c.group,
      source: c.source,
      derived: c.derived,
      length: c.samples.length,
    })),
  };

  const headerJson = new TextEncoder().encode(JSON.stringify(header));

  // Total payload float count.
  let floatCount = session.time.length + session.distance.length;
  for (const c of session.channels) floatCount += c.samples.length;

  const payloadBytes = floatCount * 8;
  const total = 4 + headerJson.length + payloadBytes;
  const out = new Uint8Array(total);
  const view = new DataView(out.buffer);

  view.setUint32(0, headerJson.length, true);
  out.set(headerJson, 4);

  // Write float payloads sequentially. Use a Float64Array view aligned to an
  // 8-byte boundary via a dedicated buffer, then copy in (payload start is not
  // guaranteed 8-aligned within `out`).
  const payload = new Float64Array(floatCount);
  let offset = 0;
  payload.set(session.time, offset);
  offset += session.time.length;
  payload.set(session.distance, offset);
  offset += session.distance.length;
  for (const c of session.channels) {
    payload.set(c.samples, offset);
    offset += c.samples.length;
  }
  out.set(new Uint8Array(payload.buffer), 4 + headerJson.length);

  return out;
}

/** Deserialise bytes produced by {@link serializeSessionData} back into a session. */
export function deserializeSessionData(bytes: Uint8Array): SessionData {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const headerLen = view.getUint32(0, true);
  const headerJson = new TextDecoder().decode(bytes.subarray(4, 4 + headerLen));
  const header = JSON.parse(headerJson) as SessionHeader;
  if (header.magic !== MAGIC) {
    throw new Error('Not a TLAB session blob (bad magic).');
  }

  const payloadStart = 4 + headerLen;
  let floatCount = header.timeLength + header.distanceLength;
  for (const c of header.channels) floatCount += c.length;

  // Copy the payload into an aligned Float64Array (source offset may be unaligned).
  const payload = new Float64Array(floatCount);
  new Uint8Array(payload.buffer).set(
    bytes.subarray(payloadStart, payloadStart + floatCount * 8),
  );

  let offset = 0;
  const time = payload.slice(offset, offset + header.timeLength);
  offset += header.timeLength;
  const distance = payload.slice(offset, offset + header.distanceLength);
  offset += header.distanceLength;

  const channels: Channel[] = header.channels.map((ch) => {
    const samples = payload.slice(offset, offset + ch.length);
    offset += ch.length;
    return {
      id: ch.id,
      name: ch.name,
      unit: ch.unit,
      group: ch.group,
      source: ch.source,
      samples,
      ...(ch.derived !== undefined ? { derived: ch.derived } : {}),
    };
  });

  return {
    meta: header.meta,
    time,
    distance,
    channels,
    laps: header.laps,
    canonicalSpeedId: header.canonicalSpeedId,
  };
}
